import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, User, Mail, Phone, Shield, Trash2, Edit, Plus, UserPlus, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminLayout from "./AdminLayout";

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (roleFilter !== "all") params.append("role", roleFilter);
      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [search, roleFilter]);

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`هل أنت متأكد من رغبتك بحذف المستخدم: "${userName}" نهائياً من النظام؟`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("تم حذف المستخدم بنجاح");
      loadUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleSave = async (userData) => {
    setSaving(true);
    try {
      if (userData.id) {
        await api.put(`/admin/users/${userData.id}`, userData);
        toast.success("تم تحديث بيانات المستخدم بنجاح");
      } else {
        await api.post("/admin/users", userData);
        toast.success("تم إنشاء الحساب بنجاح وتفعيله");
      }
      setIsDialogOpen(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const roleLabels = {
    buyer: "مشتري (زبون)",
    seller: "بائع (صاحب متجر)",
    driver: "سائق توصيل",
    admin: "مدير نظام (أدمن)"
  };

  const roleBadges = {
    buyer: "bg-blue-100 text-blue-800 border-blue-200",
    seller: "bg-emerald-100 text-emerald-800 border-emerald-200",
    driver: "bg-purple-100 text-purple-800 border-purple-200",
    admin: "bg-rose-100 text-rose-800 border-rose-200 font-bold"
  };

  return (
    <AdminLayout title="إدارة المستخدمين" subtitle="عرض، إضافة، وتعديل حسابات المشترين والبائعين والسائقين">
      <Card className="border border-border/80 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">قائمة الحسابات</CardTitle>
              <CardDescription>إجمالي المستخدمين المسجلين: {users.length}</CardDescription>
            </div>
            <Button onClick={() => { setEditingUser(null); setIsDialogOpen(true); }} className="gap-2 shrink-0">
              <UserPlus className="w-4 h-4" />
              إضافة مستخدم جديد
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="بحث بالاسم أو البريد الإلكتروني أو رقم الهاتف..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="تصفية حسب الدور" />
              </SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">كافة الأدوار</SelectItem>
                <SelectItem value="buyer">المشترون فقط</SelectItem>
                <SelectItem value="seller">البائعون فقط</SelectItem>
                <SelectItem value="driver">السائقون فقط</SelectItem>
                <SelectItem value="admin">المدراء فقط</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden">
                {users.map((user) => (
                  <article key={user.id} className="rounded-2xl border border-border bg-card p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{user.name?.[0] || "U"}</div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-extrabold">{user.name || "بدون اسم"}</h3>
                        <p className="mt-1 truncate text-xs text-muted-foreground">{user.email || "-"}</p>
                        <p className="mt-1 text-xs text-muted-foreground" dir="ltr">{user.phone || "-"}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium ${roleBadges[user.role] || "bg-gray-100 text-gray-800"}`}>{roleLabels[user.role] || user.role}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-border pt-2">
                      <span className={`text-[10px] font-bold ${user.phone_verified ? "text-emerald-600" : "text-amber-600"}`}>{user.phone_verified ? "رقم موثّق" : "بانتظار التحقق"}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-9 gap-1 text-xs" onClick={() => { setEditingUser(user); setIsDialogOpen(true); }}><Edit className="h-3.5 w-3.5" /> تعديل</Button>
                        <Button variant="outline" size="sm" className="h-9 gap-1 border-destructive/20 text-xs text-destructive" onClick={() => handleDelete(user.id, user.name)}><Trash2 className="h-3.5 w-3.5" /> حذف</Button>
                      </div>
                    </div>
                  </article>
                ))}
                {users.length === 0 && <div className="rounded-2xl bg-muted p-8 text-center text-sm text-muted-foreground">لا يوجد أي مستخدم مطابق للبحث</div>}
              </div>
              <div className="hidden overflow-hidden rounded-xl border md:block">
                <table className="w-full text-sm text-right">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="p-3.5 font-semibold">المستخدم</th>
                      <th className="p-3.5 font-semibold">بيانات الاتصال</th>
                      <th className="p-3.5 font-semibold">الدور</th>
                      <th className="p-3.5 font-semibold">حالة التحقق</th>
                      <th className="p-3.5 font-semibold">تاريخ الانضمام</th>
                      <th className="p-3.5 font-semibold text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                        <td className="p-3.5 font-medium">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                              {user.name?.[0] || "U"}
                            </div>
                            <div>
                              <p className="font-semibold text-foreground leading-tight">{user.name || "بدون اسم"}</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">{user.address || "العنوان غير محدد"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3.5">
                          <p className="font-mono text-xs text-foreground">{user.email || "-"}</p>
                          <p className="font-mono text-xs text-muted-foreground mt-0.5" dir="ltr">{user.phone || "-"}</p>
                        </td>
                        <td className="p-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border font-medium ${roleBadges[user.role] || "bg-gray-100 text-gray-800"}`}>
                            {roleLabels[user.role] || user.role}
                          </span>
                        </td>
                        <td className="p-3.5">
                          {user.phone_verified ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" /> مفعّل
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                              <Clock className="w-3.5 h-3.5" /> معلق
                            </span>
                          )}
                        </td>
                        <td className="p-3.5 text-xs text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('ar-SY') : "-"}
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => { setEditingUser(user); setIsDialogOpen(true); }}
                              title="تعديل الحساب"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(user.id, user.name)}
                              title="حذف الحساب"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">
                          لا يوجد أي مستخدم مطابق للبحث
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* User Form Modal */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? "تعديل حساب المستخدم" : "إنشاء حساب جديد"}</DialogTitle>
            <DialogDescription>
              {editingUser ? "تعديل البيانات الأساسية والصلاحيات لهذا الحساب" : "إدخال بيانات المستخدم وتعيين دوره في النظام"}
            </DialogDescription>
          </DialogHeader>
          <UserForm 
            user={editingUser} 
            saving={saving}
            onSave={handleSave} 
            onClose={() => { setIsDialogOpen(false); setEditingUser(null); }} 
          />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function UserForm({ user, saving, onSave, onClose }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: "buyer",
    address: "",
    phone_verified: true
  });

  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        email: user.email || "",
        name: user.name || "",
        phone: user.phone || "",
        role: user.role || "buyer",
        address: user.address || "",
        phone_verified: user.phone_verified ?? true,
        password: ""
      });
    } else {
      setFormData({
        email: "",
        password: "",
        name: "",
        phone: "+9639",
        role: "buyer",
        address: "",
        phone_verified: true
      });
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user && !formData.password) {
      toast.error("كلمة المرور مطلوبة لإنشاء مستخدم جديد");
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      <div>
        <Label htmlFor="u_name">الاسم الكامل *</Label>
        <Input
          id="u_name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="مثال: أحمد العبدالله"
          required
        />
      </div>
      <div>
        <Label htmlFor="u_email">البريد الإلكتروني *</Label>
        <Input
          id="u_email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="user@example.com"
          dir="ltr"
          required
        />
      </div>
      <div>
        <Label htmlFor="u_password">{user ? "تغيير كلمة المرور (اتركه فارغاً للإبقاء عليها)" : "كلمة المرور *"}</Label>
        <Input
          id="u_password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="••••••••"
          required={!user}
        />
      </div>
      <div>
        <Label htmlFor="u_phone">رقم الواتساب (سوري) *</Label>
        <Input
          id="u_phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="+9639XXXXXXXX"
          dir="ltr"
          required
        />
      </div>
      <div>
        <Label htmlFor="u_role">الدور في النظام *</Label>
        <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
          <SelectTrigger id="u_role" dir="rtl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="buyer">مشتري (زبون عادي)</SelectItem>
            <SelectItem value="seller">بائع (يمتلك متجر)</SelectItem>
            <SelectItem value="driver">سائق (عامل توصيل)</SelectItem>
            <SelectItem value="admin">مدير نظام (Super Admin)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="u_address">العنوان الافتراضي</Label>
        <Input
          id="u_address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="مثال: كفرنبل - الشارع العام"
        />
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" type="button" onClick={onClose} disabled={saving}>
          إلغاء
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          {user ? "حفظ التعديلات" : "إضافة الحساب"}
        </Button>
      </div>
    </form>
  );
}
